import React, { useCallback } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { Banner } from '@packages/ui/layout';
import { Button } from '@packages/ui/primitives/Button';
import { Inline } from '@packages/ui/layout/Inline';
import { COLORS, spacing } from '@packages/ui/tokens';
import type { Wealthlane_CapTable_Dtos_CharterFiling_CharterFilingDto } from '@wealthlane/api-services/wealthlane-captable-service';

const DocumentLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${COLORS.Purple};
  cursor: pointer;
  font-weight: 500;
  background: none;
  border: none;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }

  .material-symbols-outlined {
    font-size: 16px;
  }
`;

const DeleteLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: ${COLORS.Silver};
  cursor: pointer;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid ${COLORS.LightGray};
  background: ${COLORS.White};
  white-space: nowrap;

  &:hover {
    color: ${COLORS.Red};
    border-color: ${COLORS.Red};
    background: #fef2f2;
  }

  .material-symbols-outlined {
    font-size: 14px;
  }
`;

interface CharterFilingBannersProps {
  activeFiling?: Wealthlane_CapTable_Dtos_CharterFiling_CharterFilingDto;
  selectedFiling?: Wealthlane_CapTable_Dtos_CharterFiling_CharterFilingDto;
  isViewingHistory: boolean;
  onDocumentDownload: (documentDetailId: string) => void;
  onDeleteFiling: (filingId: string) => void;
  onShowActive: () => void;
}

export function CharterFilingBanners({
  activeFiling,
  selectedFiling,
  isViewingHistory,
  onDocumentDownload,
  onDeleteFiling,
  onShowActive,
}: CharterFilingBannersProps) {
  const renderDocuments = useCallback(
    (filing: Wealthlane_CapTable_Dtos_CharterFiling_CharterFilingDto) => (
      <Inline gap={spacing.md}>
        {(filing.documents ?? []).map((doc) => (
          <DocumentLink
            key={doc.id}
            type='button'
            onClick={() => doc.documentDetailId && onDocumentDownload(doc.documentDetailId)}
          >
            <span className='material-symbols-outlined'>description</span>
            {doc.name ?? 'Document'}
          </DocumentLink>
        ))}
      </Inline>
    ),
    [onDocumentDownload],
  );

  if (isViewingHistory && selectedFiling) {
    return (
      <Banner variant='info' semanticId='charterHistoryBanner'>
        <Inline justify='between' align='center' wrap gap={spacing.md} style={{ width: '100%' }}>
          <span>
            Viewing historical snapshot from{' '}
            {selectedFiling.filingDate
              ? moment(selectedFiling.filingDate).format('MMM DD, YYYY')
              : 'unknown date'}
            {selectedFiling.isActive ? ' (Active)' : ''}
          </span>
          <Inline gap={spacing.md} align='center' wrap>
            {renderDocuments(selectedFiling)}
            <Button variant='link' label='Show Active' onClick={onShowActive} semanticId='showActiveFilingBtn' />
            {selectedFiling.id && (
              <DeleteLink type='button' onClick={() => onDeleteFiling(selectedFiling.id!)}>
                <span className='material-symbols-outlined'>delete</span>
                Delete Current
              </DeleteLink>
            )}
          </Inline>
        </Inline>
      </Banner>
    );
  }

  if (!activeFiling) return null;

  return (
    <Banner variant='success' semanticId='charterActiveFilingBanner'>
      <Inline justify='between' align='center' wrap gap={spacing.md} style={{ width: '100%' }}>
        <Inline gap={spacing.md} align='center' wrap>
          <strong>
            Current Filing:{' '}
            {activeFiling.filingDate
              ? moment(activeFiling.filingDate).format('MMM DD, YYYY')
              : 'Unknown Date'}
          </strong>
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: COLORS.White,
              background: COLORS.Green,
              padding: '2px 8px',
              borderRadius: 10,
              textTransform: 'uppercase',
            }}
          >
            Active
          </span>
        </Inline>
        <Inline gap={spacing.md} align='center' wrap>
          {renderDocuments(activeFiling)}
          {activeFiling.id && (
            <DeleteLink type='button' onClick={() => onDeleteFiling(activeFiling.id!)}>
              <span className='material-symbols-outlined'>delete</span>
              Delete Current
            </DeleteLink>
          )}
        </Inline>
      </Inline>
    </Banner>
  );
}
